import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { SessionStepProps } from './SessionFlowBase';
import styled from 'styled-components';
import { TextField } from '@material-ui/core';
import SingleDatePicker from '../Util/SingleDatePicker';
import moment, { Moment } from 'moment';
import { AccountType, Privilege } from '../../api';
import useHasPrivilege from '../Util/useHasPrivilege';
import { useDropzone } from 'react-dropzone';
import SelectSinglePitch, { Pitch } from '../Util/SelectSinglePitch';
import { useUpdateSession } from './useUpdateSession';
import SelectSingleTeam from '../Util/SelectSingleTeam';
import { TSession } from './Sessions';
import { CompoundedComponent } from './Lineup/SessionLineups';
import SelectSingleAccount, { Account } from '../Util/SelectSingleAccount';
import useSession, { refreshSession } from '../Util/useSession';
import { Reference, toMillis } from '../../services/sessionModels';
import sessionsPrepareService from '../../services/sessionsPrepare';

interface Props extends SessionStepProps {}

const playedAt = (session: TSession | null | undefined) => {
  const millis = toMillis(session?.matchStartTime);
  return millis === undefined ? undefined : moment(millis);
};

const SessionSessionInformation = forwardRef<any, Props>(({ sessionid, setNextEnabled }, ref) => {
  const { session } = useSession(sessionid);
  const [date, setDate] = useState<Moment>(playedAt(session) || moment());
  const [pitch, setPitch] = useState<Pick<Pitch, 'id'> | undefined>(
    session?.pitch ? { id: session.pitch.id } : undefined
  );
  const [account, setAccount] = useState<Pick<Account, 'id'> | undefined>(session?.account || undefined);
  const [teamHome, setTeamHome] = useState<Reference | undefined>(session?.homeTeam || undefined);
  const [teamAway, setTeamAway] = useState<Reference | undefined>(session?.awayTeam || undefined);
  const [competition, setCompetition] = useState<string | undefined>(session?.competition || undefined);
  const [homeScore, setHomeScore] = useState<number | undefined>(session?.homeScore ?? undefined);
  const [awayScore, setAwayScore] = useState<number | undefined>(session?.awayScore ?? undefined);
  const [label, setLabel] = useState<string | undefined>(session?.label || undefined);
  const [matchInfoURL, setMatchInfoURL] = useState<string | undefined>(session?.matchInfoUrl || undefined);
  const [updateSession] = useUpdateSession();
  const isAdmin = useHasPrivilege(Privilege.Admin);

  useImperativeHandle(
    ref,
    () => ({
      onSave() {
        let ret = true;
        updateSession(
          {
            homeTeamId: teamHome?.id,
            awayTeamId: teamAway?.id,
            matchStartTime: date.toISOString(),
            pitchId: pitch?.id ? Number(pitch.id) : null,
            competition: competition,
            label: label,
            homeScore: homeScore,
            awayScore: awayScore,
            matchInfoUrl: matchInfoURL,
            ...(isAdmin ? { accountId: account?.id } : {}),
          },
          session
        );
        return ret;
      },
    }),
    [
      competition,
      session,
      date,
      matchInfoURL,
      pitch,
      account,
      isAdmin,
      teamAway,
      teamHome,
      label,
      homeScore,
      awayScore,
      updateSession,
    ]
  );

  useEffect(() => {
    if (session?.pitch && session.pitch.id && (!pitch || pitch.id !== session.pitch.id)) {
      setPitch({ id: session.pitch.id });
    }
    //eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    if (session?.account && (!account || account.id !== session.account.id)) {
      setAccount(session.account);
    }
    //eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    if (session?.homeTeam && session.homeTeam.id && (!teamHome || teamHome.id !== session.homeTeam.id)) {
      setTeamHome(session.homeTeam);
    }
    //eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    if (session?.awayTeam && session.awayTeam.id && (!teamAway || teamAway.id !== session.awayTeam.id)) {
      setTeamAway(session.awayTeam);
    }
    //eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    const sessionDate = playedAt(session);
    if (sessionDate && !date.isSame(sessionDate)) {
      setDate(sessionDate);
    }
    //eslint-disable-next-line
  }, [session]);

  useEffect(() => {
    if (session?.competition) {
      setCompetition(session.competition);
    }
  }, [session]);

  useEffect(() => {
    if (session?.label) {
      setLabel(session.label);
    }
  }, [session]);

  useEffect(() => {
    if (session && session.homeScore !== null) {
      setHomeScore(session.homeScore ?? undefined);
    }
  }, [session]);

  useEffect(() => {
    if (session && session.awayScore !== null) {
      setAwayScore(session.awayScore ?? undefined);
    }
  }, [session]);

  useEffect(() => {
    if (teamAway && teamHome && date && competition && pitch) {
      setNextEnabled(true);
    } else {
      setNextEnabled(false);
    }
  }, [session, date, pitch, setNextEnabled, teamAway, teamHome, competition]);

  const onChange = useCallback(
    (newdate: Moment) => {
      setDate(newdate);
    },
    [setDate]
  );

  const onDropAccepted = useCallback(
    async (files: File[]) => {
      const xml = await files[0].text();
      setNextEnabled(false);
      try {
        await sessionsPrepareService.importOptaF7(sessionid, xml);
        await refreshSession(sessionid);
        setNextEnabled(true);
      } catch (err) {
        console.log(err);
      }
    },
    [sessionid, setNextEnabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['.xml'],
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    onDropAccepted,
  });

  return (
    <Form {...getRootProps()}>
      <input {...getInputProps()} />
      {isDragActive && <DropOverlay>Drop the Opta F7 XML to fill in the teams and lineups</DropOverlay>}
      <Grid>
        <SelectSinglePitch value={pitch} onChange={setPitch} />
        {isAdmin && <SelectSingleAccount value={account} onChange={setAccount} types={[AccountType.Customer]} />}
        <SingleDatePicker enablePast title="Date" label="Date/time" value={date} onChange={onChange} />
        <TextField
          value={competition || ''}
          onChange={(e) => setCompetition(e.target.value)}
          name="competition"
          label="Competition"
          fullWidth
        />
        <SelectSingleTeam value={teamHome?.id || null} onChange={setTeamHome} label="Home team" addTeam />
        <SelectSingleTeam value={teamAway?.id || null} onChange={setTeamAway} label="Away team" addTeam />
        <TextField
          value={homeScore ?? ''}
          onChange={(e) => setHomeScore(parseInt(e.target.value))}
          name="homeScore"
          label="Home score"
          type="number"
          InputProps={{ inputProps: { min: 0, max: 99 } }}
          fullWidth
        />
        <TextField
          value={awayScore ?? ''}
          onChange={(e) => setAwayScore(parseInt(e.target.value))}
          name="awayScore"
          label="Away score"
          type="number"
          InputProps={{ inputProps: { min: 0, max: 99 } }}
          fullWidth
        />
        <TextField
          value={label || ''}
          onChange={(e) => setLabel(e.target.value)}
          name="label"
          label="Label (optional)"
          fullWidth
        />
        <TextField
          value={matchInfoURL || ''}
          onChange={(e) => setMatchInfoURL(e.target.value)}
          name="matchInfoURL"
          label="Match info URL"
          type="url"
          placeholder="https://example.com"
          fullWidth
        />
      </Grid>
    </Form>
  );
}) as CompoundedComponent;

const Form = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  outline: none;
`;

const DropOverlay = styled.div`
  position: absolute;
  inset: -8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: #1d4ed8;
  background: rgba(239, 246, 255, 0.92);
  border: 2px dashed #60a5fa;
  border-radius: 8px;
  pointer-events: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 32px;
  align-items: end;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

SessionSessionInformation.isComplete = (session: TSession) => {
  return !!(session.awayTeam && session.homeTeam && session.matchStartTime && session.competition && session.pitch);
};
export default SessionSessionInformation;
